// ═══════════════════════════════════════════════════════════════════════════════
// AI Prodavac - Agent Header Component
// Viktor avatar, version selector, and metadata
// ═══════════════════════════════════════════════════════════════════════════════

import { Bot, ChevronDown, Eye } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { formatUiDate } from '@/lib/ui-format';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface AgentHeaderProps {
  version: {
    id: string;
    name: string;
    version: string;
    displayName: string;
    isActive: boolean;
    createdAt: string;
    activatedAt: string | null;
    description: string | null;
  };
  systemPrompt: string;
  allVersions: Array<{
    id: string;
    displayName: string;
    version: string;
    isActive: boolean;
  }>;
  onVersionChange: (versionId: string) => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export function AgentHeader({
  version,
  systemPrompt,
  allVersions,
  onVersionChange,
}: AgentHeaderProps): JSX.Element {
  return (
    <Card className="border border-border p-6 shadow-elevation-1">
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row">
        {/* Left: Avatar + Info */}
        <div className="flex items-start gap-4">
          {/* Viktor Avatar */}
          <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-2xl bg-primary/15 text-primary shadow-elevation-2">
            <Bot className="h-8 w-8" strokeWidth={2} />
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <h2 className="text-xl font-semibold text-foreground">
                {version.displayName}
              </h2>
              
              {version.isActive && (
                <Badge className="border border-status-success/30 bg-status-success/10 text-status-success">
                  Active
                </Badge>
              )}
            </div>

            <p className="text-sm text-muted-foreground mb-2">
              Tacit AI Sales Representative
            </p>

            {version.description && (
              <p className="text-xs text-muted-foreground mb-2">
                {version.description}
              </p>
            )}

            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span>
                Since: {formatUiDate(version.createdAt)}
              </span>
              <span className="text-status-info">•</span>
              <span>Methodology: SPIN + Challenger + Sandler</span>
            </div>
          </div>
        </div>

        {/* Right: Version Selector + Actions */}
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:flex-shrink-0">
          {/* View Prompt Dialog */}
          <Dialog>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="min-h-11 gap-2 md:min-h-11"
              >
                <Eye className="w-4 h-4" />
                View Prompt
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[80vh] max-w-3xl overflow-y-auto">
              <DialogHeader>
                <DialogTitle>System Prompt - {version.displayName}</DialogTitle>
                <DialogDescription>
                  Current persona and conversation logic
                </DialogDescription>
              </DialogHeader>
              <pre
                className="overflow-x-auto whitespace-pre-wrap rounded-lg bg-muted p-4 font-mono text-xs"
                data-content-origin="domain"
              >
                {systemPrompt || 'No system prompt available'}
              </pre>
            </DialogContent>
          </Dialog>

          {/* Version Selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="min-h-11 gap-2 md:min-h-11"
              >
                v{version.version}
                <ChevronDown className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {allVersions.map((v) => (
                <DropdownMenuItem
                  key={v.id}
                  onClick={() => onVersionChange(v.id)}
                  className="flex items-center justify-between"
                  disabled={v.id === version.id}
                >
                  <span>{v.displayName}</span>
                  {v.isActive && (
                    <Badge variant="outline" className="text-xs">
                      Active
                    </Badge>
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </Card>
  );
}
